// Multi-input dropdown with arrangement feature

import { useState, useEffect } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
} from '@floating-ui/react';
import GetClickOutside from './GetClickOutside';
import MultiInputItem from './MultiInputItem';
import MultiInputItemEdit from './MultiInputItemEdit';
import ChevronUpDown from './ChevronUpDown';
import { useModal } from './ModalProvider';
import Spinner from './Spinner';
import './MultiInput.scss';

export default function MultiInput({
  value,
  onChange,
  small,
  className,
  placeholder,
  error,
  defaultNew,
  sortOnly,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [zIndex, setZIndex] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [spinner, setSpinner] = useState(false);
  const [getClickOut, setGetClickOut] = useState(false);
  const { openModal } = useModal();

  // Setup Floating UI to position dropdown within viewport and ignore parent overflow
  const { refs, floatingStyles, update } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ['top-start', 'top-end'] }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            minWidth: `${Math.round(rects.reference.width)}px`,
            maxWidth: 'min(92vw, 640px)',
            maxHeight: `${Math.max(64, Math.floor(availableHeight - 2))}px`,
            overflowY: 'auto',
          });
        },
      }),
    ],
  });

  // Keep floating position in sync when dropdown visibility or content changes
  useEffect(() => {
    if (isOpen) update?.();
  }, [isOpen, update, addMode, spinner]);

  const onChangeWrap = async (...args) => {
    setSpinner(true);
    await onChange(...args);
    setSpinner(false);
  };

  useEffect(() => {
    if (isOpen) {
      setZIndex(true);
    } else setTimeout(() => setZIndex(false), 300);
  }, [isOpen]);

  return (
    <div
      className={
        'multi-input' +
        (small ? ' small' : '') +
        (className ? ' ' + className : '')
      }
    >
      <GetClickOutside
        active={getClickOut}
        onClick={() => {
          setIsOpen(false);
          setGetClickOut(false);
        }}
      >
        <div
          ref={refs.setReference} // Bind trigger element as reference for Floating UI
          className={'input-field' + (error ? ' error' : '')}
          onClick={() => {
            setIsOpen(!isOpen);
            setGetClickOut(!isOpen);
          }}
          role='button'
          aria-haspopup='dialog'
          aria-expanded={isOpen}
          aria-label={
            placeholder && value.length > 0
              ? `${placeholder}: ${value.join(', ')}`
              : placeholder
          }
        >
          <p className={'value' + (value.length > 0 ? '' : ' placeholder')}>
            {value.length > 0 ? value.join(', ') : placeholder}
          </p>
          <ChevronUpDown up={isOpen} />
          <p className={'error-msg' + (error ? ' visible' : '')}>{error}</p>
          <p className={'label' + (value.length > 0 ? ' visible' : '')}>{placeholder}</p>
        </div>

        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={
              'multi-input-floating multi-select-floating' + // Keep click-outside ignore logic compatible
              (isOpen ? ' open' : '') +
              (zIndex ? ' z-index' : '') +
              (small ? ' small' : '')
            }
          >
            {value.map((item, index) => (
              <MultiInputItem
                key={index}
                item={item}
                small={small}
                onEdit={(newItem) => {
                  onChangeWrap(
                    [
                      ...value.slice(0, index),
                      newItem,
                      ...value.slice(index + 1),
                    ],
                    {
                      action: 'edit',
                      index,
                      item: newItem,
                    }
                  );
                }}
                onDelete={async () => {
                  setGetClickOut(false);
                  await openModal({
                    header: 'Delete',
                    body: (
                      <p className='font-16'>
                        {'Are you sure you want to delete '}
                        <span className='font-medium'>{item}</span>?
                      </p>
                    ),
                    buttons: [
                      {
                        text: 'Cancel',
                        className: 'bw',
                      },
                      {
                        text: 'Delete',
                        className: 'danger',
                        onClick: async () => {
                          await onChangeWrap(
                            [
                              ...value.slice(0, index),
                              ...value.slice(index + 1),
                            ],
                            {
                              action: 'delete',
                              index,
                            }
                          );
                        },
                      },
                    ],
                  });
                  setGetClickOut(true);
                }}
                onUp={() => {
                  if (index === 0) return;
                  const result = [...value];
                  [result[index - 1], result[index]] = [
                    result[index],
                    result[index - 1],
                  ];
                  onChangeWrap(result, {
                    action: 'up',
                    index,
                  });
                }}
                onDown={() => {
                  if (index === value.length - 1) return;
                  const result = [...value];
                  [result[index], result[index + 1]] = [
                    result[index + 1],
                    result[index],
                  ];
                  onChangeWrap(result, {
                    action: 'down',
                    index,
                  });
                }}
                sortOnly={sortOnly}
              />
            ))}

            {!sortOnly && (
              <div className={'add-wrap small'}>
                <button
                  className={'add-btn small bw' + (addMode ? ' add-mode' : '')}
                  onClick={() => setAddMode(true)}
                >
                  Add
                </button>
                <MultiInputItemEdit
                  className='add-edit'
                  onClose={() => setAddMode(false)}
                  onSave={(newItem) => {
                    onChangeWrap([...value, newItem], {
                      action: 'add',
                      index: value.length,
                      item: newItem,
                    });
                    setAddMode(false);
                  }}
                  small
                  show={addMode}
                  initValue={defaultNew}
                />
              </div>
            )}

            <Spinner show={spinner} />
          </div>
        </FloatingPortal>
      </GetClickOutside>
    </div>
  );
}
